import axios, { type AxiosError, type AxiosResponse } from 'axios';

interface ResponseErrorData {
  error?: unknown;
  code?: string;
  message?: string | string[];
}

type UnauthorizedHandler = (error: AxiosError<ResponseErrorData>) => void;

const MOJIBAKE_MARKERS = [
  '\u00C3\u00A7',
  '\u00C3\u00A3',
  '\u00C3\u00A1',
  '\u00C3\u00A9',
  '\u00C3\u00AA',
  '\u00C3\u00AD',
  '\u00C3\u00B3',
  '\u00C3\u00B4',
  '\u00C3\u00B5',
  '\u00C3\u00BA',
  '\u00C3\u0087',
  '\u00C3\u0089',
  '\u00C3\u201C',
  '\u00C3\u0161',
  '\u00C2\u00BA',
  '\u00C2\u00AA',
  '\u00C2\u00B0',
  '\u00C2\u00B2',
  '\u00C2\u00B3',
  '\u00C2\u00B7',
  '\u00C2\u00AB',
  '\u00C2\u00BB',
  'm\u00C2\u00B2',
  '\u00E2\u20AC\u201C',
  '\u00E2\u20AC\u201D',
  '\u00E2\u20AC\u0153',
  '\u00E2\u20AC\u009D',
  '\u00E2\u20AC\u02DC',
  '\u00E2\u20AC\u2122',
  '\u00E2\u20AC\u00A2',
  '\u00E2\u20AC\u00A6',
  '\u00E2\u201E\u00A2',
];

const PUBLIC_AUTH_PATHS = ['/auth/me', '/auth/login', '/auth/logout', '/auth/forgot-password', '/auth/reset-password'];

let unauthorizedHandler: UnauthorizedHandler | undefined;

function tryRepairMojibake(value: string): string {
  if (!MOJIBAKE_MARKERS.some((marker) => value.includes(marker))) {
    return value;
  }

  try {
    const bytes = Uint8Array.from(Array.from(value).map((char) => char.charCodeAt(0) & 0xff));
    const repaired = new TextDecoder('utf-8').decode(bytes);
    return repaired.includes('\uFFFD') ? value : repaired;
  } catch {
    return value;
  }
}

function normalizeEncoding<T>(input: T): T {
  if (typeof input === 'string') {
    return tryRepairMojibake(input) as T;
  }

  if (Array.isArray(input)) {
    return input.map((item: unknown) => normalizeEncoding(item)) as T;
  }

  if (isPlainObject(input)) {
    const output: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(input)) {
      output[key] = normalizeEncoding(value);
    }
    return output as T;
  }

  return input;
}

function isPlainObject(input: unknown): input is Record<string, unknown> {
  if (!input || typeof input !== 'object') {
    return false;
  }

  const prototype: unknown = Object.getPrototypeOf(input);
  return prototype === Object.prototype || prototype === null;
}

function isBinaryLikePayload(input: unknown): boolean {
  if (!input || typeof input !== 'object') {
    return false;
  }

  if (typeof Blob !== 'undefined' && input instanceof Blob) {
    return true;
  }

  if (typeof ArrayBuffer !== 'undefined') {
    if (input instanceof ArrayBuffer) {
      return true;
    }
    if (ArrayBuffer.isView(input)) {
      return true;
    }
  }

  if (typeof FormData !== 'undefined' && input instanceof FormData) {
    return true;
  }

  return false;
}

function shouldNormalizePayload(responseType: unknown, data: unknown): boolean {
  if (responseType === 'blob' || responseType === 'arraybuffer' || responseType === 'stream') {
    return false;
  }

  return !isBinaryLikePayload(data);
}

function resolveBaseUrl(): string {
  const configuredBaseUrl = (import.meta.env.VITE_API_URL as string | undefined)?.trim();
  if (configuredBaseUrl) {
    return configuredBaseUrl;
  }

  const { hostname, protocol } = window.location;
  const isLocalHost = hostname === 'localhost' || hostname === '127.0.0.1';
  if (isLocalHost) {
    return `${protocol}//${hostname}:3333/api`;
  }

  return '/api';
}

function getRequestPath(error: AxiosError<ResponseErrorData>): string {
  return error.config?.url?.trim() ?? '';
}

function shouldNotifyUnauthorized(error: AxiosError<ResponseErrorData>): boolean {
  if (error.response?.status !== 401) {
    return false;
  }

  const requestPath = getRequestPath(error);
  return !PUBLIC_AUTH_PATHS.some((path) => requestPath.includes(path));
}

export function setUnauthorizedHandler(handler?: UnauthorizedHandler) {
  unauthorizedHandler = handler;
}

export const api = axios.create({
  baseURL: resolveBaseUrl(),
  headers: {
    'Content-Type': 'application/json;charset=utf-8',
  },
  withCredentials: true,
});

api.interceptors.response.use(
  (response: AxiosResponse<unknown>) => {
    if (shouldNormalizePayload(response.config.responseType, response.data)) {
      response.data = normalizeEncoding(response.data);
    }
    return response;
  },
  async (error: AxiosError<ResponseErrorData>) => {
    if (shouldNotifyUnauthorized(error)) {
      unauthorizedHandler?.(error);
    } else if (error.response?.status === 304) {
      error.response.data = {
        error: 'Não houve nenhuma alteração no servidor.',
      };
    } else if (error.response?.status === 403) {
      error.response.data = {
        error: 'Não tem permissão para essa ação.',
      };
    }

    if (error.response?.data && shouldNormalizePayload(error.response.config.responseType, error.response.data)) {
      error.response.data = normalizeEncoding(error.response.data);
    }

    return await Promise.reject(error);
  }
);
